<template>
  <!--
    工作台：参考原型「dashboard」页
    KPI / 业务助理 / 客户漏斗 / 销售漏斗 / 业务审批 / 日历
    统计口径：KPI 与漏斗为当前商户全量；业务审批与日历只看当前登录职员。
  -->
  <div class="person-dashboard">
    <list-page>
      <list-table background-color="transparent">
        <div v-loading="loading" class="dash-body">
          <!-- 顶部工具条 -->
          <div class="dash-toolbar">
            <div class="dash-title">
              <span class="title">工作台</span>
              <span class="sub">{{ todayLabel }} · 数据实时统计</span>
            </div>
            <page-button type="read" @click="loadAll">刷新</page-button>
          </div>

          <div class="dash-grid">
            <!-- ==================== 主区 ==================== -->
            <div class="dash-main">
              <!-- KPI -->
              <div class="kpi-grid">
                <div v-for="k in kpiList" :key="k.key" class="kpi-card"
                     :class="{clickable: !!k.route}" @click="go(k.route)">
                  <div class="kpi-icon" :style="{background: k.bg, color: k.color}">
                    <el-icon><component :is="k.icon"/></el-icon>
                  </div>
                  <div class="kpi-body">
                    <div class="kpi-label">{{ k.label }}</div>
                    <div class="kpi-value">
                      {{ k.value }}<span class="unit">{{ k.unit }}</span>
                    </div>
                    <div class="kpi-trend">
                      <span class="muted">环比</span>
                      <span v-if="k.rate === null" class="flat">—</span>
                      <span v-else :class="rateClass(k.rate)">
                        {{ rateText(k.rate) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 业务助理 -->
              <div class="dash-card">
                <div class="card-head">
                  <div class="card-title">业务助理<span class="card-sub">(个)</span></div>
                  <el-select v-model="period" size="small" class="period-select">
                    <el-option label="全部" value="ALL"/>
                    <el-option label="本月" value="MONTH"/>
                    <el-option label="本季" value="QUARTER"/>
                    <el-option label="本年" value="YEAR"/>
                  </el-select>
                </div>
                <div class="assistant-grid">
                  <div v-for="a in assistantList" :key="a.itemKey" class="assistant-cell">
                    <div class="assistant-label">
                      {{ a.label }}<span class="muted2">({{ a.unit }})</span>
                    </div>
                    <div class="assistant-val">{{ a.display }}</div>
                    <div class="assistant-foot">
                      <span>环比</span>
                      <span v-if="a.rate === null" class="flat">—</span>
                      <span v-else :class="rateClass(a.rate)">{{ rateText(a.rate) }}</span>
                    </div>
                    <div class="assistant-bar">
                      <i :style="{width: Math.min(100, Math.abs(a.rate || 0)) + '%', background: barColor(a.rate)}"/>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 仪表盘 -->
              <div class="dash-card">
                <div class="card-head">
                  <div class="card-title">仪表盘</div>
                  <span class="icon-btn" title="刷新图表" @click="refreshCharts">
                    <el-icon><Refresh/></el-icon>
                  </span>
                </div>

                <div class="divider-t"><span class="dot" style="background:#1677ff"/>客户漏斗分布</div>
                <!-- 图表容器一直保留：刷新后数据从空变非空时，echarts 实例仍绑在同一个 DOM 上 -->
                <div class="funnel-block">
                  <div ref="customerChart" class="donut-chart"/>
                  <div class="hbar-list">
                    <div v-if="!customerRows.length" class="chart-empty">暂无客户数据</div>
                    <div v-for="(row, i) in customerRows" :key="i" class="hbar-row">
                      <span class="hbar-name" :title="row.name">{{ row.name }}</span>
                      <span class="hbar-track">
                        <i :style="{width: row.width + '%', background: row.color}"/>
                      </span>
                      <span class="hbar-val">{{ row.count }}</span>
                    </div>
                  </div>
                </div>

                <div class="divider-t"><span class="dot" style="background:#0958d9"/>销售漏斗分析</div>
                <div class="funnel-block">
                  <div ref="opportunityChart" class="funnel-chart"/>
                  <div class="funnel-legend">
                    <div v-for="(row, i) in opportunityRows" :key="i" class="fl-row">
                      <span class="fl-name">{{ row.name }}</span>
                      <span class="fl-track">
                        <i :style="{width: row.width + '%', background: funnelColor(i)}"/>
                      </span>
                      <span class="fl-val">{{ row.count }}</span>
                      <span class="fl-rate">{{ row.rateText }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ==================== 侧栏 ==================== -->
            <div class="dash-side">
              <!-- 业务审批 -->
              <div class="dash-card approval-card">
                <div class="card-head">
                  <div class="card-title">业务审批<span class="card-sub">({{ approvalCount + copyCount }})</span></div>
                  <span class="link" @click="go('/project/view/check')">全部</span>
                </div>
                <div class="sub-tabs">
                  <div class="sub-tab" :class="{active: approvalTab === 'APPROVAL'}"
                       @click="approvalTab = 'APPROVAL'">待我处理 ({{ approvalCount }})
                  </div>
                  <div class="sub-tab" :class="{active: approvalTab === 'COPY'}"
                       @click="approvalTab = 'COPY'">知会我的 ({{ copyCount }})
                  </div>
                </div>
                <div class="approval-list">
                  <div v-if="!filteredApprovals.length" class="empty-block">暂无数据</div>
                  <div v-for="a in filteredApprovals" :key="a.id" class="approval-item" @click="go(a.route)">
                    <span class="appr-dot" :style="{background: a.workType === 'APPROVAL' ? '#e5484d' : '#1677ff'}"/>
                    <div class="appr-main">
                      <div class="appr-title" :title="a.title">{{ a.title || '审批流程' }}</div>
                      <div class="appr-meta">
                        <span>{{ a.sourceTypeName || '工作流' }}</span>
                        <span>{{ timeText(a.dueDate) }}</span>
                        <span class="badge" :class="a.level === '紧急' ? 'red' : 'gray'">{{ a.level }}</span>
                      </div>
                    </div>
                    <span class="appr-action">{{ a.workType === 'APPROVAL' ? '处理' : '查看' }}</span>
                  </div>
                </div>
              </div>

              <!-- 日历 -->
              <div v-loading="calendarLoading" class="dash-card calendar-card">
                <div class="cal-head">
                  <div class="cal-title">{{ calendarMonthLabel }}</div>
                  <div class="cal-nav">
                    <span class="nav-btn" title="上月" @click="shiftMonth(-1)">‹</span>
                    <span class="nav-btn" title="回到本月" @click="toCurrentMonth">今</span>
                    <span class="nav-btn" title="下月" @click="shiftMonth(1)">›</span>
                  </div>
                </div>
                <div class="cal-grid">
                  <div v-for="w in weekDays" :key="w" class="cal-wd">{{ w }}</div>
                  <template v-for="(cell, i) in calendarCells" :key="i">
                    <div v-if="!cell" class="cal-day blank"/>
                    <div v-else class="cal-day"
                         :class="{today: cell.isToday, off: cell.holidayType === 1, work: cell.holidayType === 2}"
                         :title="cell.tip">
                      <span class="cal-num">{{ cell.day }}</span>
                      <i v-if="cell.scheduleCount" class="cal-dot"
                         :class="{done: cell.doneCount >= cell.scheduleCount}"/>
                      <span v-if="cell.holidayText" class="cal-holiday">{{ cell.holidayText }}</span>
                    </div>
                  </template>
                </div>
                <div class="cal-legend">
                  <span><i class="dot today"/>今天</span>
                  <span><i class="dot plan"/>有日程</span>
                  <span><i class="dot done"/>已完成</span>
                </div>
                <div class="cal-actions">
                  <page-button type="read" @click="go('/project/view/calendar')">查看日历</page-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </list-table>
    </list-page>
  </div>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import {useRouter} from "vue-router";
import {Bell, Document, Money, Refresh, User} from "@element-plus/icons-vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import dashboardApi from "@/api/dz-crm/person/dashboardApi";
import commonApi from "@/api/commonApi";
import {DICT_CUSTOMER_TYPE, DICT_OPPORTUNITY_STAGE, fillStageRows} from "@/views/h5/crm/crmDictionary";

/** KPI 卡：图标、配色、跳转目标都在前端维护，后端只给数值与环比 */
const KPI_META = [
  {key: "customer", label: "客户数量", unit: "家", icon: markRaw(User), bg: "#e8f2ff", color: "#1677ff", route: "/customer/list"},
  {key: "expiring", label: "即将到期提醒", unit: "条", icon: markRaw(Bell), bg: "#eaf9ee", color: "#22a55a", route: "/customer/expire"},
  {key: "contract", label: "合同数量", unit: "份", icon: markRaw(Document), bg: "#fff4e3", color: "#f09819", route: "/contract/list"},
  {key: "contractAmount", label: "合同总金额", unit: "元", icon: markRaw(Money), bg: "#e9f4ff", color: "#2f7bff", route: "/contract/list"},
];

/** 业务助理指标：名称、单位、颜色由前端维护 */
const ASSISTANT_META = {
  NEW_CUSTOMER: {label: "新增客户", unit: "个", color: "#1677ff"},
  NEW_CONTACT: {label: "新增联系人", unit: "个", color: "#13b8b8"},
  NEW_OPPORTUNITY: {label: "新增商机", unit: "个", color: "#faad14"},
  NEW_CONTRACT_AMOUNT: {label: "新增合同金额", unit: "元", color: "#52c41a"},
};

const CUSTOMER_COLORS = ["#1677ff", "#13b8b8", "#52c41a", "#faad14", "#7a5af8", "#c3ccd9"];
const FUNNEL_COLORS = ["#69b1ff", "#4096ff", "#1677ff", "#0958d9", "#003eb3", "#002c8c"];

/** 当前月份 yyyy-MM */
function currentMonthString() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** 当前日期 yyyy-MM-dd（不用 toISOString，避免时区把日期挪一天） */
function currentDateString() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default {
  setup() {
    return {router: useRouter()};
  },
  name: "PersonDashboard",
  components: {PageButton, ListTable, ListPage, Refresh},
  data() {
    return {
      loading: false,
      overview: {},
      overviewLoaded: false,
      dictLoaded: false,
      customerTypeList: [],
      stageDict: [],
      period: "MONTH",
      approvalTab: "APPROVAL",
      customerChart: null,
      opportunityChart: null,
      calendarMonth: currentMonthString(),
      calendarData: {},
      calendarLoading: false,
      weekDays: ["日", "一", "二", "三", "四", "五", "六"],
    };
  },
  computed: {
    todayLabel() {
      const d = new Date();
      const week = ["星期日", "星期一", "星期二", "星期三", "星期四", "星期五", "星期六"][d.getDay()];
      return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 ${week}`;
    },
    todayDateString() {
      return currentDateString();
    },
    kpiList() {
      const d = this.overview || {};
      return KPI_META.map(meta => {
        let value = 0;
        let rate = null;
        switch (meta.key) {
          case "customer":
            value = this.formatNumber(d.customerCount);
            rate = d.customerCountRate;
            break;
          case "expiring":
            value = this.formatNumber(d.expiringCount);
            rate = d.expiringCountRate;
            break;
          case "contract":
            value = this.formatNumber(d.contractCount);
            rate = d.contractCountRate;
            break;
          case "contractAmount":
            value = this.compactMoney(d.contractAmount);
            rate = d.contractAmountRate;
            break;
          default:
            break;
        }
        return {...meta, value, rate: this.toNumberOrNull(rate)};
      });
    },
    assistantList() {
      const stats = this.overview.assistantStats || {};
      const list = stats[this.period] || [];
      return list.map(item => {
        const meta = ASSISTANT_META[item.itemKey] || {label: item.itemKey, unit: "", color: "#1677ff"};
        const value = Number(item.value || 0);
        const display = item.itemKey === "NEW_CONTRACT_AMOUNT"
            ? "¥" + this.compactMoney(value)
            : this.formatNumber(value);
        return {
          ...item,
          ...meta,
          display,
          rate: this.toNumberOrNull(item.rate),
        };
      });
    },
    /** 客户类型分布：后端只给字典值，这里按客户类型字典映射名称 */
    customerRows() {
      const stats = this.overview.customerTypeStats || [];
      const dictMap = {};
      (this.customerTypeList || []).forEach(d => dictMap[String(d.userCode)] = d.dictName);

      const rows = stats.map(s => {
        const key = s.dimValue === null || s.dimValue === undefined ? "" : String(s.dimValue);
        return {
          name: key ? (dictMap[key] || "其他") : "未填写",
          count: Number(s.dimCount || 0),
        };
      }).filter(r => r.count > 0).sort((a, b) => b.count - a.count);

      const max = rows.length ? rows[0].count : 0;
      return rows.map((r, i) => ({
        ...r,
        color: CUSTOMER_COLORS[i % CUSTOMER_COLORS.length],
        width: max ? (r.count / max * 100).toFixed(1) : "0",
      }));
    },
    customerTotalDisplay() {
      return this.formatNumber(this.overview.customerTotal || 0);
    },
    /** 商机阶段：按阶段字典补全、剔除已流失，漏斗顺序跟字典一致 */
    opportunityRows() {
      const stageStats = this.overview.opportunityStageStats || [];
      let rows;
      if (this.stageDict && this.stageDict.length) {
        rows = fillStageRows(this.stageDict, stageStats, {excludeClose: true})
            .map(r => ({name: r.stageTitle || "未命名阶段", count: Number(r.opportunityCount || 0)}));
      } else if (this.dictLoaded) {
        // 字典确实没配置时至少让数据可见，名称退化成阶段值；字典还在加载时先留空，避免闪一下
        rows = stageStats
            .filter(s => Number(s.stageValue) !== 9999)
            .map(s => ({name: "阶段 " + s.stageValue, count: Number(s.opportunityCount || 0)}));
      } else {
        rows = [];
      }
      const first = rows.length ? rows[0].count : 0;
      return rows.map((r, i) => {
        const prev = i === 0 ? null : rows[i - 1].count;
        let rateText = "—";
        if (prev !== null) {
          rateText = prev ? (r.count / prev * 100).toFixed(1) + "%" : "—";
        }
        return {
          ...r,
          width: first ? Math.min(100, r.count / first * 100).toFixed(1) : "0",
          rateText,
          color: FUNNEL_COLORS[i % FUNNEL_COLORS.length],
        };
      });
    },
    approvalCount() {
      return this.overview.approvalCount || 0;
    },
    copyCount() {
      return this.overview.copyCount || 0;
    },
    filteredApprovals() {
      return (this.overview.approvalList || []).filter(a => a.workType === this.approvalTab);
    },
    calendarMonthLabel() {
      const [year, month] = this.calendarMonth.split("-");
      return `${year}年 ${Number(month)}月`;
    },
    /** 日历格子：前置空格 + 当月天数，标记今天、日程、放假调休 */
    calendarCells() {
      const [year, month] = this.calendarMonth.split("-").map(Number);
      const first = new Date(year, month - 1, 1);
      const offset = first.getDay();
      const dayCount = new Date(year, month, 0).getDate();

      const dayMap = {};
      (this.calendarData.dayList || []).forEach(d => dayMap[d.dayDate] = d);
      const holidayMap = {};
      (this.calendarData.holidayList || []).forEach(h => holidayMap[h.dayDate] = h);

      const cells = [];
      for (let i = 0; i < offset; i++) {
        cells.push(null);
      }
      for (let day = 1; day <= dayCount; day++) {
        const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        const item = dayMap[dateStr];
        const holiday = holidayMap[dateStr];
        const scheduleCount = item ? Number(item.scheduleCount || 0) : 0;
        const doneCount = item ? Number(item.doneCount || 0) : 0;
        const tipParts = [];
        if (scheduleCount) {
          tipParts.push(`日程 ${scheduleCount} 条`);
        }
        if (holiday && holiday.holidayName) {
          tipParts.push(holiday.holidayName);
        }
        cells.push({
          day,
          date: dateStr,
          isToday: dateStr === this.todayDateString,
          scheduleCount,
          doneCount,
          holidayType: holiday ? Number(holiday.dayType) || 0 : 0,
          holidayText: holiday ? (Number(holiday.dayType) === 2 ? "班" : "休") : "",
          tip: tipParts.join(" · "),
        });
      }
      return cells;
    },
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
    this.loadDictionaries();
    this.loadAll();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    if (this.customerChart) {
      this.customerChart.dispose();
      this.customerChart = null;
    }
    if (this.opportunityChart) {
      this.opportunityChart.dispose();
      this.opportunityChart = null;
    }
  },
  methods: {
    async loadAll() {
      this.loading = true;
      try {
        await Promise.all([this.loadOverview(), this.loadCalendar()]);
      } finally {
        this.loading = false;
      }
    },
    async loadOverview() {
      const res = await dashboardApi.overview();
      this.overview = res || {};
      this.overviewLoaded = true;
      this.$nextTick(() => this.tryRenderCharts());
    },
    async loadDictionaries() {
      try {
        const [customerTypes, stages] = await Promise.all([
          commonApi.queryUniqueDictionaryList(DICT_CUSTOMER_TYPE),
          commonApi.queryUniqueDictionaryList(DICT_OPPORTUNITY_STAGE),
        ]);
        this.customerTypeList = customerTypes || [];
        this.stageDict = stages || [];
      } finally {
        this.dictLoaded = true;
        this.$nextTick(() => this.tryRenderCharts());
      }
    },
    async loadCalendar() {
      const month = this.calendarMonth;
      this.calendarLoading = true;
      try {
        const res = await dashboardApi.calendar({month});
        // 连续切月时后发的请求可能先回来，只认当前月份的响应
        if (month === this.calendarMonth) {
          this.calendarData = res || {};
        }
      } finally {
        this.calendarLoading = false;
      }
    },
    tryRenderCharts() {
      if (!this.overviewLoaded) {
        return;
      }
      this.renderCustomerChart();
      this.renderOpportunityChart();
    },
    renderCustomerChart() {
      const dom = this.$refs.customerChart;
      if (!dom) {
        return;
      }
      if (!this.customerChart) {
        this.customerChart = markRaw(echarts.init(dom));
      }
      this.customerChart.setOption({
        tooltip: {trigger: "item", appendToBody: true, formatter: "{b}：{c} ({d}%)"},
        series: [{
          type: "pie",
          radius: ["62%", "84%"],
          center: ["50%", "50%"],
          avoidLabelOverlap: false,
          itemStyle: {borderColor: "#fff", borderWidth: 2},
          label: {show: false},
          labelLine: {show: false},
          data: this.customerRows.map(r => ({
            name: r.name,
            value: r.count,
            itemStyle: {color: r.color},
          })),
        }],
        graphic: [
          {
            type: "text",
            left: "center",
            top: "40%",
            style: {
              text: this.customerTotalDisplay,
              fill: "#303133",
              fontSize: 24,
              fontWeight: 600,
              textAlign: "center",
            },
          },
          {
            type: "text",
            left: "center",
            top: "54%",
            style: {text: "客户数量", fill: "#909399", fontSize: 12, textAlign: "center"},
          },
        ],
      }, true);
    },
    renderOpportunityChart() {
      const dom = this.$refs.opportunityChart;
      if (!dom) {
        return;
      }
      if (!this.opportunityChart) {
        this.opportunityChart = markRaw(echarts.init(dom));
      }
      const data = this.opportunityRows
          .filter(r => r.count > 0)
          .map(r => ({
            name: r.name,
            value: r.count,
            itemStyle: {color: r.color},
          }));
      this.opportunityChart.setOption({
        tooltip: {trigger: "item", appendToBody: true, formatter: "{b}：{c}"},
        series: [{
          type: "funnel",
          left: "8%",
          right: "8%",
          top: 10,
          bottom: 10,
          minSize: "24%",
          sort: "none",
          label: {show: true, position: "inside", formatter: "{b}", fontSize: 12},
          itemStyle: {borderColor: "#fff", borderWidth: 1},
          data,
        }],
        graphic: data.length ? [] : [{
          type: "text",
          left: "center",
          top: "middle",
          style: {text: "暂无商机数据", fill: "#909399", fontSize: 13},
        }],
      }, true);
    },
    refreshCharts() {
      this.$nextTick(() => this.tryRenderCharts());
    },
    handleResize() {
      if (this.customerChart) {
        this.customerChart.resize();
      }
      if (this.opportunityChart) {
        this.opportunityChart.resize();
      }
    },
    shiftMonth(delta) {
      const [year, month] = this.calendarMonth.split("-").map(Number);
      const d = new Date(year, month - 1 + delta, 1);
      this.calendarMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      this.loadCalendar();
    },
    toCurrentMonth() {
      this.calendarMonth = currentMonthString();
      this.loadCalendar();
    },
    go(route) {
      if (route) {
        this.router.push(route).catch(() => {
        });
      }
    },
    funnelColor(index) {
      return FUNNEL_COLORS[index % FUNNEL_COLORS.length];
    },
    toNumberOrNull(value) {
      if (value === null || value === undefined || value === "") {
        return null;
      }
      const n = Number(value);
      return Number.isNaN(n) ? null : n;
    },
    rateClass(rate) {
      if (rate > 0) {
        return "up";
      }
      return rate < 0 ? "down" : "flat";
    },
    rateText(rate) {
      if (rate === null || rate === undefined) {
        return "—";
      }
      const n = Number(rate);
      if (n === 0) {
        return "0.00%";
      }
      return (n > 0 ? "↑ " : "↓ ") + Math.abs(n).toFixed(2) + "%";
    },
    barColor(rate) {
      if (rate > 0) {
        return "linear-gradient(90deg,#9ee0b6,#22a55a)";
      }
      if (rate < 0) {
        return "linear-gradient(90deg,#ffb3b5,#e5484d)";
      }
      return "#dfe5ee";
    },
    formatNumber(value) {
      const n = Number(value || 0);
      return n.toLocaleString("zh-CN");
    },
    /** 金额紧凑展示：亿 / 万，避免 KPI 卡被长数字撑破 */
    compactMoney(value) {
      const n = Number(value || 0);
      if (Math.abs(n) >= 100000000) {
        return (n / 100000000).toFixed(2).replace(/\.?0+$/, "") + "亿";
      }
      if (Math.abs(n) >= 10000) {
        return (n / 10000).toFixed(2).replace(/\.?0+$/, "") + "万";
      }
      return n.toLocaleString("zh-CN", {maximumFractionDigits: 2});
    },
    timeText(value) {
      if (!value) {
        return "—";
      }
      const text = String(value).replace("T", " ");
      if (text.slice(0, 10) === this.todayDateString) {
        return "今天 " + text.slice(11, 16);
      }
      return text.slice(5, 16);
    },
  },
};
</script>

<style lang="scss" scoped>
.person-dashboard {
  height: 100%;

  .dash-body {
    padding: 12px;
    min-height: 100%;
    box-sizing: border-box;
  }
}

/* ---------- 顶部 ---------- */
.dash-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  .dash-title {
    display: flex;
    align-items: baseline;
    gap: 10px;

    .title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
    }

    .sub {
      font-size: 12px;
      color: #909399;
    }
  }
}

/* ---------- 栅格 ---------- */
.dash-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 12px;
  align-items: start;
}

.dash-main,
.dash-side {
  min-width: 0;
}

.dash-main {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.dash-side {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ---------- KPI ---------- */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.kpi-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--yq-border-color, #e0e2e9);
  border-radius: 8px;
  background: #fff;
  transition: box-shadow .16s, transform .16s;

  &.clickable {
    cursor: pointer;

    &:hover {
      box-shadow: 0 4px 14px rgba(22, 119, 255, .12);
      transform: translateY(-1px);
    }
  }

  .kpi-icon {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
  }

  .kpi-body {
    min-width: 0;
    flex: 1;
  }

  .kpi-label {
    font-size: 12.5px;
    color: #606266;
    margin-bottom: 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .kpi-value {
    font-size: 22px;
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: -.4px;
    font-variant-numeric: tabular-nums;
    color: #303133;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    .unit {
      margin-left: 4px;
      font-size: 12px;
      font-weight: 400;
      color: #909399;
    }
  }

  .kpi-trend {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;
    font-size: 11.5px;
    color: #909399;
  }
}

/* ---------- 卡片 ---------- */
.dash-card {
  border: 1px solid var(--yq-border-color, #e0e2e9);
  border-radius: 8px;
  background: #fff;
  padding: 14px 16px;
  min-width: 0;
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;

  .card-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
  }

  .card-sub {
    font-size: 12px;
    font-weight: 400;
    color: #909399;
    margin-left: 2px;
  }

  .period-select {
    width: 92px;
  }

  .link {
    font-size: 12px;
    color: #1677ff;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  color: #909399;
  cursor: pointer;

  &:hover {
    color: #1677ff;
    background: #f2f7ff;
  }
}

/* ---------- 业务助理 ---------- */
.assistant-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 4px 8px;
}

.assistant-cell {
  padding: 12px 8px;
  border-radius: 8px;

  &:hover {
    background: #f8fbff;
  }

  .assistant-label {
    font-size: 12.5px;
    color: #606266;
    margin-bottom: 6px;

    .muted2 {
      color: #a8abb2;
    }
  }

  .assistant-val {
    font-size: 21px;
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: -.4px;
    font-variant-numeric: tabular-nums;
    color: #303133;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .assistant-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 7px;
    font-size: 11.5px;
    color: #909399;
  }

  .assistant-bar {
    height: 4px;
    border-radius: 4px;
    background: #eef2f8;
    overflow: hidden;
    margin-top: 8px;

    i {
      display: block;
      height: 100%;
      border-radius: 4px;
      transition: width .8s cubic-bezier(.4, 0, .2, 1);
    }
  }
}

/* ---------- 仪表盘 ---------- */
.divider-t {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin: 14px 0 10px;

  &:first-of-type {
    margin-top: 0;
  }
}

.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
}

.funnel-block {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

.donut-chart {
  flex: 0 0 240px;
  width: 240px;
  height: 220px;
}

.funnel-chart {
  flex: 0 0 300px;
  width: 300px;
  height: 240px;
}

.chart-empty,
.empty-block {
  color: #909399;
  font-size: 12.5px;
  padding: 16px 0;
}

.hbar-list {
  flex: 1;
  min-width: 220px;
}

.hbar-row {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 32px;
  font-size: 12.5px;

  .hbar-name {
    width: 92px;
    text-align: right;
    color: #606266;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .hbar-track {
    flex: 1;
    height: 10px;
    border-radius: 5px;
    background: #f2f5f9;
    overflow: hidden;

    i {
      display: block;
      height: 100%;
      border-radius: 5px;
      transition: width .6s;
    }
  }

  .hbar-val {
    width: 44px;
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: #303133;
    font-weight: 600;
  }
}

.funnel-legend {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 200px;
}

.fl-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12.3px;

  .fl-name {
    width: 72px;
    color: #606266;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .fl-track {
    flex: 1;
    height: 8px;
    border-radius: 4px;
    background: #f2f5f9;
    overflow: hidden;

    i {
      display: block;
      height: 100%;
      border-radius: 4px;
      transition: width .6s;
    }
  }

  .fl-val {
    width: 40px;
    text-align: right;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: #303133;
  }

  .fl-rate {
    width: 52px;
    text-align: right;
    color: #909399;
  }
}

/* ---------- 业务审批 ---------- */
.approval-card {
  /* 固定卡片高度：待我处理/知会我的来回切换时，侧栏高度不跳动 */
  height: 380px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  .sub-tabs {
    flex: 0 0 auto;
    display: flex;
    gap: 2px;
    border-bottom: 1px solid var(--yq-border-color, #e0e2e9);
    margin: 0 -16px 4px;
    padding: 0 12px;
  }

  .sub-tab {
    padding: 8px 10px;
    font-size: 12.5px;
    color: #909399;
    cursor: pointer;
    border-bottom: 2px solid transparent;

    &.active {
      color: #1677ff;
      border-bottom-color: #1677ff;
      font-weight: 500;
    }
  }

  .approval-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    margin: 0 -16px;
  }

  /* 空状态铺满列表区并居中，避免贴着左侧边框显示 */
  .empty-block {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 16px;
    text-align: center;
    letter-spacing: normal;
  }

  .approval-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 11px 16px;
    border-bottom: 1px solid #f2f5f9;
    cursor: pointer;

    &:last-child {
      border-bottom: 0;
    }

    &:hover {
      background: #f8fbff;
    }
  }

  .appr-dot {
    flex: 0 0 8px;
    width: 8px;
    height: 8px;
    margin-top: 6px;
    border-radius: 50%;
  }

  .appr-main {
    flex: 1;
    min-width: 0;
  }

  .appr-title {
    font-size: 13px;
    color: #303133;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .appr-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
    font-size: 11.5px;
    color: #909399;

    .badge {
      padding: 0 5px;
      border-radius: 3px;
      line-height: 16px;

      &.red {
        color: #e5484d;
        background: #fef0f0;
      }

      &.gray {
        color: #909399;
        background: #f2f3f5;
      }
    }
  }

  .appr-action {
    flex: 0 0 auto;
    margin-top: 2px;
    padding: 2px 8px;
    border: 1px solid #d9ecff;
    border-radius: 4px;
    font-size: 11.5px;
    color: #1677ff;
    background: #f2f7ff;
  }
}

/* ---------- 日历 ---------- */
.calendar-card {
  .cal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;

    .cal-title {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
    }

    .cal-nav {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .nav-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border-radius: 4px;
      font-size: 15px;
      color: #606266;
      cursor: pointer;

      &:hover {
        color: #1677ff;
        background: #f2f7ff;
      }
    }
  }

  .cal-grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 2px;
  }

  .cal-wd {
    text-align: center;
    font-size: 11.5px;
    color: #a8abb2;
    padding: 4px 0;
  }

  .cal-day {
    position: relative;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    font-size: 12.5px;
    color: #303133;

    &.blank {
      background: transparent;
    }

    &.today {
      background: #1677ff;
      color: #fff;
      font-weight: 600;
    }

    &.off {
      color: #e5484d;
    }

    &.work {
      color: #22a55a;
    }

    .cal-dot {
      position: absolute;
      bottom: 4px;
      left: 50%;
      width: 4px;
      height: 4px;
      margin-left: -2px;
      border-radius: 50%;
      background: #1677ff;

      &.done {
        background: #52c41a;
      }
    }

    .cal-holiday {
      position: absolute;
      top: 2px;
      right: 3px;
      font-size: 9px;
      line-height: 1;
      color: #e5484d;
    }

    &.today .cal-holiday {
      color: #fff;
    }
  }

  .cal-legend {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-top: 10px;
    font-size: 11.5px;
    color: #909399;

    .dot {
      margin-right: 4px;

      &.today {
        background: #1677ff;
      }

      &.plan {
        background: #1677ff;
      }

      &.done {
        background: #52c41a;
      }
    }
  }

  .cal-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
  }
}

/* ---------- 涨跌色 ---------- */
.up {
  color: #22a55a;
}

.down {
  color: #e5484d;
}

.flat {
  color: #909399;
}

/* ---------- 响应式 ---------- */
@media (max-width: 1400px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 1100px) {
  .dash-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .assistant-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
